import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ToastProvider } from './contexts/ToastContext';
import MainLayout from './components/layouts/MainLayout';
import AdminLayout from './components/layouts/AdminLayout';
import RequireSuperAdmin from './components/admin/RequireSuperAdmin';
import ImpersonationBanner from './components/admin/ImpersonationBanner';
import ActivityTracker from './components/ActivityTracker';
import IncomingMessagesWatcher from './components/IncomingMessagesWatcher';
import CookieConsent from './components/CookieConsent';
import { SkeletonPageHeader, Skeleton } from './components/Skeleton';

// Eager: landing + auth entry
import Home from './pages/Home';
import Contact from './pages/Contact';
import About from './pages/About';
import Blog from './pages/Blog';
import Login from './pages/Login';
import Register from './pages/Register';
import Join from './pages/Join';
import NotFound from './pages/NotFound';

const SwapLink = lazy(() => import('./pages/SwapLink'));
const UserDocs = lazy(() => import('./pages/UserDocs'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Employees = lazy(() => import('./pages/Employees'));
const UserGroups = lazy(() => import('./pages/UserGroups'));
const MyJobs = lazy(() => import('./pages/MyJobs'));
const Shifts = lazy(() => import('./pages/Shifts'));
const CreateShift = lazy(() => import('./pages/CreateShift'));
const ShiftPresets = lazy(() => import('./pages/ShiftPresets'));
const Workplaces = lazy(() => import('./pages/Workplaces'));
const AttendanceRequests = lazy(() => import('./pages/AttendanceRequests'));
const Vacation = lazy(() => import('./pages/Vacation'));
const RecentActivity = lazy(() => import('./pages/RecentActivity'));
const MyShifts = lazy(() => import('./pages/MyShifts'));
const JobBoard = lazy(() => import('./pages/JobBoard'));
const JobApplications = lazy(() => import('./pages/JobApplications'));
const JobDetails = lazy(() => import('./pages/JobDetails'));
const CVManager = lazy(() => import('./pages/CVManager'));
const Messages = lazy(() => import('./pages/Messages'));
const EditShift = lazy(() => import('./pages/EditShift'));
const ShiftDetails = lazy(() => import('./pages/ShiftDetails'));
const CreateShiftPreset = lazy(() => import('./pages/CreateShiftPreset'));
const EditShiftPreset = lazy(() => import('./pages/EditShiftPreset'));
const Settings = lazy(() => import('./pages/Settings'));
const Positions = lazy(() => import('./pages/Positions'));
const Managers = lazy(() => import('./pages/Managers'));
const Contracts = lazy(() => import('./pages/Contracts'));
const MyContracts = lazy(() => import('./pages/MyContracts'));
const Reports = lazy(() => import('./pages/Reports'));
const Announcements = lazy(() => import('./pages/Announcements'));

const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminBusinesses = lazy(() => import('./pages/admin/AdminBusinesses'));
const AdminBusinessDetail = lazy(() => import('./pages/admin/AdminBusinessDetail'));
const AdminCredits = lazy(() => import('./pages/admin/AdminCredits'));
const AdminContacts = lazy(() => import('./pages/admin/AdminContacts'));
const AdminActivity = lazy(() => import('./pages/admin/AdminActivity'));
const AdminContracts = lazy(() => import('./pages/admin/AdminContracts'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminAnnouncements = lazy(() => import('./pages/admin/AdminAnnouncements'));

function RouteFallback() {
  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <SkeletonPageHeader />
      <Skeleton height="120px" />
      <Skeleton height="200px" />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <CurrencyProvider>
        <AuthProvider>
        <ToastProvider>
        <Router>
          <ImpersonationBanner />
          <ActivityTracker />
          <IncomingMessagesWatcher />
          <CookieConsent />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/kontakt" element={<Contact />} />
            <Route path="/o-nas" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<Blog />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/join/:code" element={<Join />} />
            <Route
              path="/swap/:token"
              element={
                <Suspense fallback={<SkeletonPageHeader />}>
                  <SwapLink />
                </Suspense>
              }
            />
            <Route path="/docs/*" element={<UserDocs />} />

            {/* Platform admin */}
            <Route
              path="/admin"
              element={
                <RequireSuperAdmin>
                  <AdminLayout />
                </RequireSuperAdmin>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:id" element={<AdminUserDetail />} />
              <Route path="businesses" element={<AdminBusinesses />} />
              <Route path="businesses/:id" element={<AdminBusinessDetail />} />
              <Route path="credits" element={<AdminCredits />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="contracts" element={<AdminContracts />} />
              <Route path="jobs" element={<AdminJobs />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Route>

            {/* Protected tenant routes */}
            <Route path="/" element={<MainLayout />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="reports" element={<Reports />} />
              <Route path="employees" element={<Employees />} />
              <Route path="user-groups" element={<UserGroups />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="messages" element={<Messages />} />
              <Route path="my-jobs" element={<MyJobs />} />
              <Route path="shifts" element={<Shifts />} />
              <Route path="shifts/:id" element={<ShiftDetails />} />
              <Route path="my-shifts" element={<MyShifts />} />
              <Route path="create-shift" element={<CreateShift />} />
              <Route path="shift-presets" element={<ShiftPresets />} />
              <Route path="shift-presets/:id/edit" element={<EditShiftPreset />} />
              <Route path="create-shift-preset" element={<CreateShiftPreset />} />
              <Route path="workplaces" element={<Workplaces />} />
              <Route path="attendance-requests" element={<AttendanceRequests />} />
              <Route path="vacation" element={<Vacation />} />
              <Route path="recent-activity" element={<RecentActivity />} />
              <Route path="job-board" element={<JobBoard />} />
              <Route path="job-board/:id" element={<JobDetails />} />
              <Route path="job-applications" element={<JobApplications />} />
              <Route path="cv-manager" element={<CVManager />} />
              <Route path="shifts/:id/edit" element={<EditShift />} />
              <Route path="settings" element={<Settings />} />
              <Route path="positions" element={<Positions />} />
              <Route path="managers" element={<Managers />} />
              <Route path="contracts" element={<Contracts />} />
              <Route path="my-contracts" element={<MyContracts />} />
            </Route>

            {/* Universal 404 */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </Router>
        </ToastProvider>
        </AuthProvider>
      </CurrencyProvider>
    </LanguageProvider>
  );
}

export default App;
